import { useState, useEffect, useRef, useCallback } from 'react';
import css from './FractionalBlogPage.module.css';
import img from '../../assets/girl-laptop.jpg';
import { createVlogs, updateVlog, deleteVlog, getFeaturedVlog, getHeroContent, updateHeroContent } from '../../util/api';
import { Modal, NamedLink } from '../../components';
import { VlogSkeleton, FeaturedVlogSkeleton } from '../../components/VlogSkeleton/VlogSkeleton';
import { toast } from 'react-toastify';

const VlogSection = ({ vlogs = [], isAdmin, onManageDisableScrolling, onVlogsUpdate, vlogsLoading = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingVlog, setEditingVlog] = useState(null);
  const [form, setForm] = useState({
    title: "",
    youtubeUrl: "",
    category: "",
    description: ""
  });
  const [preview, setPreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [showFeaturedModal, setShowFeaturedModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vlogToDelete, setVlogToDelete] = useState(null);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [heroContent, setHeroContent] = useState(null);
  const [showHeroModal, setShowHeroModal] = useState(false);
  const [heroForm, setHeroForm] = useState({
    badge: "",
    headline: "",
    subtext: "",
    buttonText: "",
    backgroundImage: null
  });
  const [heroLoading, setHeroLoading] = useState(true);
  
  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(4);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const sliderRef = useRef(null);

  // Update slides to show based on screen width
  const updateSlidesToShow = useCallback(() => {
    const width = window.innerWidth;
    if (width >= 1200) {
      setSlidesToShow(4);
    } else if (width >= 1024) {
      setSlidesToShow(3);
    } else if (width >= 768) {
      setSlidesToShow(2);
    } else {
      setSlidesToShow(1);
    }
  }, []);

  useEffect(() => {
    updateSlidesToShow();
    window.addEventListener('resize', updateSlidesToShow);
    return () => window.removeEventListener('resize', updateSlidesToShow);
  }, [updateSlidesToShow]);

  // Reset current slide when vlogs change
  useEffect(() => {
    setCurrentSlide(0);
  }, [vlogs.length]);

  // Slider navigation
  const maxSlide = Math.max(0, vlogs.length - slidesToShow);
  
  const nextSlide = () => {
    setCurrentSlide(prev => Math.min(prev + 1, maxSlide));
  };

  const prevSlide = () => {
    setCurrentSlide(prev => Math.max(prev - 1, 0));
  };

  const goToSlide = (index) => {
    setCurrentSlide(Math.min(Math.max(index, 0), maxSlide));
  };

  // Touch handlers for swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Helper function to validate if URL is YouTube or Instagram
  const isValidVideoUrl = (url) => {
    if (!url) return false;
    const youtubeRegex = /(youtube\.com|youtu\.be|youtube\.com\/shorts)/;
    const instagramRegex = /(instagram\.com\/(reel|p)\/)/;
    return youtubeRegex.test(url) || instagramRegex.test(url);
  };

  // Helper function to convert URL to embed format
  const convertToEmbedUrl = (url) => {
    if (!url) return null;
    // YouTube handling
    if (url.includes("youtube.com/watch")) {
      const videoId = url.split("v=")[1]?.split("&")[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    } else if (url.includes("youtu.be")) {
      const videoId = url.split("/").pop()?.split("?")[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }
    // YouTube Shorts handling
    else if (url.includes("youtube.com/shorts/")) {
      const videoId = url.split("/shorts/")[1]?.split("?")[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }
    // Instagram Reels handling
    else if (url.includes("instagram.com/reel") || url.includes("instagram.com/p/")) {
      const match = url.match(/\/(reel|p)\/([A-Za-z0-9_-]+)/);
      if (match && match[2]) {
        return `https://www.instagram.com/reel/${match[2]}/embed`;
      }
      return url;
    }

    return url;
  };

  // Fetch featured video on mount
  useEffect(() => {
    const fetchFeatured = async () => {
      setFeaturedLoading(true);
      try {
        const response = await getFeaturedVlog();
        if (response?.data) {
          setFeaturedVideo(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch featured video:', err);
      } finally {
        setFeaturedLoading(false);
      }
    };
    
    const fetchHeroContent = async () => {
      setHeroLoading(true);
      try {
        const response = await getHeroContent();
        if (response?.data) {
          setHeroContent(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch hero content:', err);
      } finally {
        setHeroLoading(false);
      }
    };
    
    fetchFeatured();
    fetchHeroContent();
  }, []);

  const saveVideo = async () => {
    if (!form.youtubeUrl.trim()) {
      toast.error('Video URL is required');
      return;
    }

    if (!isValidVideoUrl(form.youtubeUrl)) {
      toast.error('Only YouTube and Instagram links are allowed');
      return;
    }

    setIsSaving(true);
    try {
      const embedUrl = convertToEmbedUrl(form.youtubeUrl);

      if (editingVlog) {
        await updateVlog({
          _id: editingVlog._id,
          title: form.title,
          youtubeUrl: embedUrl,
          category: form.category,
          description: form.description
        });
        toast.success('Video updated successfully!');
      } else {
        await createVlogs({
          title: form.title,
          youtubeUrl: embedUrl,
          category: form.category,
          description: form.description
        });
        toast.success('Video added successfully!');
      }

      await onVlogsUpdate();

      setShowModal(false);
      setEditingVlog(null);
      setForm({ title: "", youtubeUrl: "", category: "", description: "" });
      setPreview(null);

    } catch (err) {
      console.error(err);
      toast.error('Failed to save video. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (vlog) => {
    setEditingVlog(vlog);
    setForm({
      title: vlog.title || "",
      youtubeUrl: vlog.embedUrl || "",
      category: vlog.category || "",
      description: vlog.description || ""
    });
    setPreview(vlog.embedUrl);
    setShowModal(true);
  };

  const handleDeleteClick = (vlog) => {
    setVlogToDelete(vlog);
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setVlogToDelete(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingVlog(null);
    setForm({ title: "", youtubeUrl: "", category: "", description: "" });
    setPreview(null);
  };

  const handleUrlChange = (url) => {
    setForm({ ...form, youtubeUrl: url });
    const embedUrl = convertToEmbedUrl(url);
    setPreview(embedUrl);
  }

  const saveFeaturedVideo = async () => {
    if (!form.youtubeUrl.trim()) {
      toast.error('Video URL is required');
      return;
    }

    if (!isValidVideoUrl(form.youtubeUrl)) {
      toast.error('Only YouTube and Instagram links are allowed');
      return;
    }

    setIsSaving(true);
    try {
      const embedUrl = convertToEmbedUrl(form.youtubeUrl);

      // Create or update featured video in MongoDB
      if (featuredVideo?._id) {
        // Update existing featured video
        const response = await updateVlog({
          _id: featuredVideo._id,
          title: form.title || "Featured Video",
          youtubeUrl: embedUrl,
          category: form.category,
          description: form.description,
          isFeatured: true
        });
        setFeaturedVideo(response.data);
      } else {
        // Create new featured video
        const response = await createVlogs({
          title: form.title || "Featured Video",
          youtubeUrl: embedUrl,
          category: form.category,
          description: form.description,
          isFeatured: true
        });
        setFeaturedVideo(response.data);
      }

      setShowFeaturedModal(false);
      setForm({ title: "", youtubeUrl: "", category: "", description: "" });
      setPreview(null);
      toast.success('Featured video updated successfully!');

    } catch (err) {
      console.error(err);
      toast.error('Failed to update featured video.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseFeaturedModal = () => {
    setShowFeaturedModal(false);
    setForm({ title: "", youtubeUrl: "", category: "", description: "" });
    setPreview(null);
  };

  const handleEditFeatured = () => {
    if (featuredVideo) {
      setForm({
        title: featuredVideo.title || "",
        youtubeUrl: featuredVideo.embedUrl || "",
        category: featuredVideo.category || "",
        description: featuredVideo.description || ""
      });
      setPreview(featuredVideo.embedUrl);
    }
    setShowFeaturedModal(true);
  };

  const handleDeleteFeatured = () => {
    setVlogToDelete(featuredVideo);
    setShowDeleteModal(true);
  };

  const handleEditHero = () => {
    if (heroContent) {
      setHeroForm({
        badge: heroContent.badge || "",
        headline: heroContent.headline || "",
        subtext: heroContent.subtext || "",
        buttonText: heroContent.buttonText || "",
        backgroundImage: heroContent.backgroundImage || null
      });
    }
    setShowHeroModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsSaving(true);
      try {
        const uploadURL = process.env.REACT_APP_CLOUDINARY_URL;
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", process.env.REACT_APP_CLOUDINARY_PRESET);
        formData.append("api_key", process.env.REACT_APP_CLOUDINARY_API_KEY);

        const cloudinaryResponse = await fetch(uploadURL, {
          method: "POST",
          body: formData,
        });

        const cloudinaryData = await cloudinaryResponse.json();
        if (cloudinaryData.secure_url) {
          setHeroForm({ ...heroForm, backgroundImage: cloudinaryData.secure_url });
          toast.success('Image uploaded successfully!');
        } else {
          toast.error('Failed to upload image to Cloudinary. The image size exceeds the 10MB limit. Please select a smaller file.', cloudinaryData?.error?.message);
        }
      } catch (err) {
        console.error('Image upload error:', err);
        toast.error('Failed to upload image');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const saveHeroContent = async () => {
    setIsSaving(true);
    try {
      const response = await updateHeroContent(heroForm);
      if (response?.data) {
        setHeroContent(response.data);
        setShowHeroModal(false);
        toast.success('Hero content updated successfully!');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to update hero content.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseHeroModal = () => {
    setShowHeroModal(false);
    setHeroForm({
      badge: "",
      headline: "",
      subtext: "",
      buttonText: "",
      backgroundImage: null
    });
  };

  const confirmDelete = async () => {
    if (!vlogToDelete) return;
    
    setDeletingId(vlogToDelete._id);
    try {
      await deleteVlog({ id: vlogToDelete._id });
      
      // If deleting featured video, clear it from state
      if (vlogToDelete._id === featuredVideo?._id) {
        setFeaturedVideo(null);
      } else {
        await onVlogsUpdate();
      }
      
      toast.success('Video deleted successfully!');
      setShowDeleteModal(false);
      setVlogToDelete(null);
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete video. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <section className={css.featuredVlogSection}>
        <h2 className={css.sectionTitle}>Featured Insight</h2>
        <section className={css.shortsHeroSection}>
          <div className={css.shortsMainContainer}>
            <div className={css.shortPlayerSide}>
              {featuredLoading ? (
                <FeaturedVlogSkeleton />
              ) : (
                <div className={css.phoneFrame}>
                  {featuredVideo?.embedUrl ? (
                    <>
                      <iframe
                        className={css.shortIframe}
                        src={featuredVideo.embedUrl}
                        title="Featured Video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                        allowFullScreen
                        referrerPolicy="strict-origin-when-cross-origin"
                      />
                      {isAdmin && (
                        <div className={css.featuredActions}>
                          <button 
                            className={css.featuredEditBtn}
                            onClick={handleEditFeatured}
                            title="Edit Featured Video"
                          >
                            Edit
                          </button>
                          <button 
                            className={css.featuredDeleteBtn}
                            onClick={handleDeleteFeatured}
                            title="Delete Featured Video"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {isAdmin && (
                        <button 
                          className={css.addFeaturedBtnInside} 
                          onClick={() => setShowFeaturedModal(true)}
                          title="Add Featured Video"
                        >
                          <span className={css.plusIcon}>+</span>
                          <span className={css.addVideoText}>Add Video</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className={css.contentSideCard} style={{ backgroundImage: `url(${heroContent?.backgroundImage || img})` }}>
              <div className={css.premiumOverlay}>
                <span className={css.badgeTag}>{heroContent?.badge || "60-SECOND STRATEGY"}</span>
                <h1 className={css.heroHeadline}>{heroContent?.headline || "Revolutionizing the Fractional Economy"}</h1>
                <p className={css.heroSubtext}>
                  {heroContent?.subtext || "Explore how we are redefining professional security in the era of blended work models and fractional leadership."}
                </p>
                <NamedLink className={css.primaryBtn} name="LandingPage" > 
                    {heroContent?.buttonText || "EXPLORE THE PLATFORM"}
                </NamedLink>
                
                
              </div>

              {isAdmin && (
                  <button 
                    className={css.heroEditBtn}
                    onClick={handleEditHero}
                    title="Edit Hero Content"
                  >
                    Edit
                  </button>
                )}
            </div>
          </div>
        </section>
      </section>

      <section className={css.recentVideosSection}>
        <div className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>Recent Insights</h2>
          {vlogs?.length > slidesToShow && (
            <div className={css.sliderControls}>
              <button 
                className={css.sliderArrow} 
                onClick={prevSlide}
                disabled={currentSlide === 0}
                aria-label="Previous slide"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className={css.sliderArrow} 
                onClick={nextSlide}
                disabled={currentSlide >= maxSlide}
                aria-label="Next slide"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}
        </div>

        <div 
          className={css.sliderContainer}
          ref={sliderRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {vlogsLoading ? (
            <div className={css.sliderTrack}>
              <VlogSkeleton count={slidesToShow} slidesToShow={slidesToShow} />
            </div>
          ) : (
            <div 
              className={css.sliderTrack}
              style={{
                transform: `translateX(-${currentSlide * (100 / slidesToShow)}%)`,
              }}
            >
              {Array.isArray(vlogs) && vlogs?.map((item) => (
                <div 
                  key={item?._id} 
                  className={css.shortCard}
                  style={{ flex: `0 0 ${100 / slidesToShow}%` }}
                >
                  <div className={css.shortThumbnailWrapper}>
                    <iframe
                      className={css.shortIframe}
                      src={item?.embedUrl}
                      title={item?.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                      allowFullScreen
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  </div>

                  <div className={css.shortCardInfo}>
                    {item?.category && (
                      <span className={css.shortCategory}>{item?.category}</span>
                    )}
                    {item?.title && (
                      <h5 className={css.shortCardTitle}>{item?.title}</h5>
                    )}
                    {item?.description && (
                      <p className={css.shortCardDesc}>{item?.description}</p>
                    )}
                    
                    {isAdmin && (
                      <div className={css.adminActions}>
                        <button 
                          className={css.editBtn}
                          onClick={() => handleEdit(item)}
                          disabled={deletingId === item?._id}
                        >
                          Edit
                        </button>
                        <button 
                          className={css.deleteBtn}
                          onClick={() => handleDeleteClick(item)}
                          disabled={deletingId === item?._id}
                        >
                          {deletingId === item?._id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination Dots */}
        {vlogs?.length > slidesToShow && !vlogsLoading && (
          <div className={css.paginationDots}>
            {Array.from({ length: maxSlide + 1 }).map((_, index) => (
              <button
                key={index}
                className={`${css.dot} ${currentSlide === index ? css.activeDot : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {isAdmin && (
          <div className={css.addVlogBtnWrapper}>
            <button className={css.addVlogBtn} onClick={() => setShowModal(true)}>
              + Add Vlog
            </button>
          </div>
        )}
      </section>

      <Modal
        id="VlogPage.vlogModal"
        isOpen={!!showModal}
        onClose={handleCloseModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {showModal && (
          <div className={css.modalContent}>
            <h3 className={css.modalTitle}>
              {editingVlog ? 'Edit Video' : 'Add Video'}
            </h3>
            
            <div className={css.formGroup}>
              <label className={css.formLabel}>
                Video URL <span className={css.required}>*</span>
              </label>
              <input
                className={css.formInput}
                placeholder="YouTube or Instagram URL (e.g., https://www.youtube.com/... or https://www.instagram.com/reel/...)"
                value={form.youtubeUrl}
                onChange={(e) => handleUrlChange(e.target.value)}
                required
              />
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel}>Title (Optional)</label>
              <input
                className={css.formInput}
                placeholder="Enter video title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel}>Category (Optional)</label>
              <input
                className={css.formInput}
                placeholder="e.g., CAREER TIP, STRATEGY, etc."
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel}>Short Description (Optional)</label>
              <textarea
                className={css.formTextarea}
                placeholder="Brief description of the video"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
              />
            </div>

            {preview && (
              <div className={css.previewWrapper}>
                <iframe 
                  src={preview} 
                  className={css.previewIframe}
                  title="Preview" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            )}

            <div className={css.modalActions}>
              <button 
                className={css.saveBtn} 
                onClick={saveVideo} 
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : (editingVlog ? 'Update' : 'Save')}
              </button>
              <button 
                className={css.cancelBtn} 
                onClick={handleCloseModal} 
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        id="VlogPage.featuredVlogModal"
        isOpen={!!showFeaturedModal}
        onClose={handleCloseFeaturedModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {showFeaturedModal && (
          <div className={css.modalContent}>
            <h3 className={css.modalTitle}>Add Featured Video</h3>
            
            <div className={css.formGroup}>
              <label className={css.formLabel}>
                Video URL <span className={css.required}>*</span>
              </label>
              <input
                className={css.formInput}
                placeholder="YouTube or Instagram URL (e.g., https://www.youtube.com/... or https://www.instagram.com/reel/...)"
                value={form.youtubeUrl}
                onChange={(e) => handleUrlChange(e.target.value)}
                required
              />
            </div>

            {preview && (
              <div className={css.previewWrapper}>
                <iframe 
                  src={preview} 
                  className={css.previewIframe}
                  title="Preview" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            )}

            <div className={css.modalActions}>
              <button 
                className={css.saveBtn} 
                onClick={saveFeaturedVideo} 
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button 
                className={css.cancelBtn} 
                onClick={handleCloseFeaturedModal} 
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        id="VlogPage.deleteModal"
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.modalContent}>
          <h3 className={css.modalTitle}>Delete Video</h3>
          <p className={css.deleteMessage}>
            Are you sure you want to delete this video?
            {vlogToDelete?.title && (
              <strong className={css.deleteVideoTitle}> "{vlogToDelete.title}"</strong>
            )}
          </p>
          <div className={css.modalActions}>
            <button 
              className={css.deleteConfirmBtn} 
              onClick={confirmDelete}
              disabled={deletingId === vlogToDelete?._id}
            >
              {deletingId === vlogToDelete?._id ? 'Deleting...' : 'Delete'}
            </button>
            <button 
              className={css.cancelBtn} 
              onClick={cancelDelete}
              disabled={deletingId === vlogToDelete?._id}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        id="VlogPage.heroModal"
        isOpen={!!showHeroModal}
        onClose={handleCloseHeroModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {showHeroModal && (
          <div className={css.modalContent}>
            <h3 className={css.modalTitle}>Edit Hero Section</h3>
            
            <div className={css.formGroup}>
              <label className={css.formLabel}>Badge Text</label>
              <input
                className={css.formInput}
                placeholder="e.g., 60-SECOND STRATEGY"
                value={heroForm.badge}
                onChange={(e) => setHeroForm({ ...heroForm, badge: e.target.value })}
              />
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel}>Headline</label>
              <input
                className={css.formInput}
                placeholder="e.g., Revolutionizing the Fractional Economy"
                value={heroForm.headline}
                onChange={(e) => setHeroForm({ ...heroForm, headline: e.target.value })}
              />
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel}>Subtext</label>
              <textarea
                className={css.formTextarea}
                placeholder="Enter hero section description"
                value={heroForm.subtext}
                onChange={(e) => setHeroForm({ ...heroForm, subtext: e.target.value })}
                rows={3}
              />
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel}>Button Text</label>
              <input
                className={css.formInput}
                placeholder="e.g., EXPLORE THE PLATFORM"
                value={heroForm.buttonText}
                onChange={(e) => setHeroForm({ ...heroForm, buttonText: e.target.value })}
              />
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel}>Background Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className={css.formInput}
                disabled={isSaving}
              />
              {heroForm.backgroundImage && (
                <div className={css.imagePreview}>
                  <img 
                    src={heroForm.backgroundImage} 
                    alt="Background preview" 
                    style={{ maxWidth: '100%', maxHeight: '200px', marginTop: '10px' }}
                  />
                </div>
              )}
            </div>

            <div className={css.modalActions}>
              <button 
                className={css.saveBtn} 
                onClick={saveHeroContent} 
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button 
                className={css.cancelBtn} 
                onClick={handleCloseHeroModal} 
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>

    </>
  );
};

export default VlogSection;